import { AlertTriangle, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

// ──── Value Chain Node (simplified, dark) ────
// A clean circular icon node: coloured ring by health, stage label,
// a one-word sublabel, and the headline percentage. All the deep
// detail (deltas, maturity ceilings, modeled sources, peer verdicts)
// now lives behind the node's click-through — the node itself is a
// calm signal + a navigation affordance, not a data dump.
export default function ChainNode({
  label, sublabel, score, icon: Icon, suffix = '%', onClick,
  isLeak = false, spark, emptyHint, onPeekClick, statusHint, onConstraintClick,
  deltaPct, deltaLabel = 'vs last quarter',
  // legacy props (kept so the caller doesn't break) — intentionally
  // unused now that detail is drill-down only:
  // eslint-disable-next-line no-unused-vars
  color, info, deltaDirection, leakCallout, ceilingFactor, headroomPillar, modeled,
}) {
  // ROI (suffix='x') maps a multiplier to a health band; everything
  // else is already a 0–100 health percentage.
  const healthScore = suffix === 'x'
    ? (score >= 3 ? 80 : score >= 1 ? 55 : score > 0 ? 30 : 0)
    : Math.min(score, 100);
  const testid = `chain-node-${label.toLowerCase().replace(/\s/g, '-')}`;
  const hasDelta = deltaPct !== null && deltaPct !== undefined && !Number.isNaN(Number(deltaPct));
  const dNum = Number(deltaPct);
  const dUp = hasDelta && dNum > 0;
  const dDown = hasDelta && dNum < 0;
  const sparkPts = Array.isArray(spark) && spark.length >= 2
    ? (() => {
        const vals = spark.map(Number).filter((v) => !Number.isNaN(v));
        if (vals.length < 2) return null;
        const min = Math.min(...vals); const max = Math.max(...vals);
        const range = max - min || 1;
        return vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${24 - ((v - min) / range) * 22 - 1}`).join(' ');
      })()
    : null;

  const tone = healthScore >= 80 ? 'emerald' : healthScore >= 50 ? 'amber' : healthScore > 0 ? 'rose' : 'slate';
  const ringCls = {
    emerald: 'border-emerald-500/80',
    amber: 'border-amber-500/80',
    rose: 'border-rose-500/80',
    slate: 'border-slate-600',
  }[tone];
  const fgCls = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
    slate: 'text-slate-500',
  }[tone];
  const isHealthy = tone === 'emerald';

  return (
    <div className="relative flex flex-col items-center flex-1 min-w-0 group" data-testid={`${testid}-wrap`}>
      {/* Primary constraint callout — a single red badge + dashed
          connector dropping onto the constrained node. */}
      {isLeak && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-30 flex flex-col items-center"
          data-testid={`${testid}-constraint-callout`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onConstraintClick?.(); }}
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/70 bg-rose-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-300 whitespace-nowrap hover:bg-rose-500/25 hover:border-rose-400 hover:text-rose-200 transition-colors cursor-pointer"
            data-testid={`${testid}-constraint-btn`}
            title="Open the focused fix-plan for this stage"
          >
            <AlertTriangle className="h-3 w-3" /> Primary Constraint
          </button>
          <span className="h-5 w-px border-l border-dashed border-rose-500/70 pointer-events-none" aria-hidden="true" />
        </div>
      )}

      <button
        onClick={onClick}
        className="flex flex-col items-center gap-2 w-full cursor-pointer focus:outline-none"
        data-testid={testid}
        title={`Open ${label} detail`}
      >
        <div className="relative">
          <div className={cn(
            'h-14 w-14 rounded-full border-2 flex items-center justify-center bg-[#0b1220] transition-all duration-200 group-hover:scale-105',
            isLeak ? 'border-orange-400 shadow-[0_0_18px_2px_rgba(249,115,22,0.4)]' : ringCls,
          )}>
            <Icon className={cn('h-6 w-6', fgCls)} />
          </div>
          {isHealthy && !isLeak && (
            <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-emerald-500 ring-2 ring-[#0f1729] flex items-center justify-center" aria-hidden="true">
              <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
            </span>
          )}
        </div>
        <div className="text-center">
          <div className="min-h-[2rem] flex flex-col justify-start">
            <p className="text-[11px] font-bold uppercase tracking-tight text-slate-200 leading-tight whitespace-nowrap">{label}</p>
            {sublabel && <p className="text-[10px] text-slate-400 leading-tight whitespace-nowrap">{sublabel}</p>}
          </div>
          {score > 0 ? (
            <p className={cn('text-xl font-extrabold mt-0.5 tabular-nums', fgCls)}>
              {score}<span className="text-sm font-bold">{suffix}</span>
            </p>
          ) : (
            <div className="mt-0.5">
              <p className="text-lg font-semibold text-slate-600">--</p>
              {emptyHint && <p className="text-[9px] leading-tight text-amber-400/90 mt-0.5 max-w-[92px] mx-auto">{emptyHint}</p>}
            </div>
          )}
        </div>
      </button>

      {/* Hover peek — a small trend + number so the user can sense the
          movement before committing to the full drill-down. */}
      <div
        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-40 w-max max-w-[190px] opacity-0 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-150 group-hover:opacity-100"
        data-testid={`${testid}-hover-peek`}
      >
        <div className="rounded-lg border border-[#22304d] bg-[#0b1220] px-3 py-2 shadow-xl text-center">
          {statusHint && (
            <p className="text-[11px] text-slate-200 leading-snug mb-1.5 text-left" data-testid={`${testid}-status-hint`}>
              {statusHint}
            </p>
          )}
          {hasDelta ? (
            <p className={cn('text-xs font-bold', dUp ? 'text-emerald-400' : dDown ? 'text-rose-400' : 'text-slate-300')}>
              {dUp ? '▲' : dDown ? '▼' : '▬'} {dNum > 0 ? '+' : ''}{deltaPct}%&nbsp;
              <span className="text-slate-500 font-normal">{deltaLabel}</span>
            </p>
          ) : (
            <p className="text-xs text-slate-400">No quarter-over-quarter change yet</p>
          )}
          {sparkPts && (
            onPeekClick ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPeekClick(); }}
                className="block w-[150px] mt-1.5 rounded hover:bg-white/10 transition-colors"
                title={`Open ${label} trend view`}
                data-testid={`${testid}-spark-link`}
              >
                <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="w-[150px] h-6" aria-hidden="true">
                  <polyline fill="none" stroke="#44b6d2" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={sparkPts} vectorEffect="non-scaling-stroke" />
                </svg>
              </button>
            ) : (
              <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="w-[150px] h-6 mt-1.5" data-testid={`${testid}-sparkline`} aria-hidden="true">
                <polyline fill="none" stroke="#44b6d2" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={sparkPts} vectorEffect="non-scaling-stroke" />
              </svg>
            )
          )}
          <p className="text-[10px] text-[#44b6d2] mt-0.5">Click to open {label} →</p>
        </div>
      </div>
    </div>
  );
}
